import { IconPanelRightOpen } from "@/assets/icons/geist/IconPanelRightOpen";
import { withGitpodSize } from "@/assets/icons/utils";
import { AgentExecutionDropdownMenu } from "@/components/ai/AgentExecutionDropdownMenu";
import {
    ConversationContent,
    ConversationError,
    ConversationInput,
} from "@/components/ai/chat/AgentExecutionConversation";
import { PendingInteractiveBlock } from "@/components/ai/PendingInteractiveBlock";
import { ConvertToProjectBanner } from "@/components/environments/ConvertToProjectBanner";
import { Button } from "@/components/flexkit/Button";
import { KeyboardCombo } from "@/components/keyboard/KeyboardCombo";
import { cn } from "@/components/podkit/lib/cn";
import { Tooltip } from "@/components/Tooltip";
import { useDetailsPanelToggleShortcut } from "@/hooks/use-details-panel-toggle-shortcut";
import useIsMobileViewport from "@/hooks/use-is-mobile-viewport";
import { useConversationState } from "@/hooks/use-conversation-state";
import { useBuildButtonNotification } from "@/hooks/use-build-button-notification";
import type { PlainAgentExecution } from "@/queries/agent-queries";

import { agentExecutionName } from "@/utils/agent-executions";
import { ToggleDetailsPanelKeyBinding } from "@/utils/platform";
import { IconSidebarSimpleRightSquare } from "central-icons/IconSidebarSimpleRightSquare";
import { useMemo, type FC } from "react";

const SidebarIconClosed = withGitpodSize(IconSidebarSimpleRightSquare);

export type EnvironmentAgentExecutionConversationProps = {
    agentExecution: PlainAgentExecution | null;
    isDetailsPanelOpen: boolean;
    onToggleDetailsPanel: () => void;
    environmentId?: string;
    onBuildButtonAppear?: (specFilePath: string | null) => void;
};

export const EnvironmentAgentExecutionConversation: FC<EnvironmentAgentExecutionConversationProps> = ({
    agentExecution,
    isDetailsPanelOpen,
    onToggleDetailsPanel,
    environmentId,
    onBuildButtonAppear,
}) => {
    const {
        error,
        loadingMessage,
        messages,
        pendingInteractiveBlock,
        handleNewMessage,
        handleSendMessage,
    } = useConversationState(agentExecution, environmentId);

    useBuildButtonNotification(pendingInteractiveBlock, messages, onBuildButtonAppear);

    useDetailsPanelToggleShortcut(() => {
        onToggleDetailsPanel();
    });

    return (
        <div
            data-testid="environment-agent-execution-conversation"
            className={cn("flex h-full min-w-[100px] flex-col rounded-l-lg border-border-base bg-surface-01")}
        >
            <Header
                agentExecution={agentExecution}
                isDetailsPanelOpen={isDetailsPanelOpen}
                onToggleDetailsPanel={onToggleDetailsPanel}
            />
            {environmentId && <ConvertToProjectBanner environmentId={environmentId} />}

            <ConversationError error={error} loadingMessage={loadingMessage} />
            <ConversationContent
                messages={messages}
                agentExecution={agentExecution}
                environmentId={environmentId}
                onSendMessage={handleSendMessage}
                pendingInteractiveBlock={pendingInteractiveBlock}
            />
            <ConversationInput
                hasMessages={messages.length > 0}
                agentExecution={agentExecution}
                onNewMessage={(message) => handleNewMessage(message, messages)}
                messages={messages}
                environmentId={environmentId}
                pendingInteractiveBlock={
                    <PendingInteractiveBlock
                        fragment={pendingInteractiveBlock}
                        onSendMessage={handleSendMessage}
                        environmentId={environmentId}
                    />
                }
                pendingInteractiveBlockType={pendingInteractiveBlock?.type}
            />
        </div>
    );
};

const Header: FC<{
    agentExecution: PlainAgentExecution | null;
    isDetailsPanelOpen: boolean;
    onToggleDetailsPanel: () => void;
}> = ({ agentExecution, isDetailsPanelOpen, onToggleDetailsPanel }) => {
    const displayName = useMemo(
        () => (agentExecution ? agentExecutionName(agentExecution) : "Conversation"),
        [agentExecution],
    );
    const { isMobileViewport: isMobile } = useIsMobileViewport();

    return (
        <div
            className={cn(
                "flex h-12 w-full items-center justify-between gap-2 border-b border-border-subtle px-4 py-2",
            )}
        >
            <div className="flex h-6 min-w-0 flex-1 items-center gap-0.5">
                {agentExecution ? (
                    <AgentExecutionDropdownMenu agentExecution={agentExecution} />
                ) : (
                    <div className="flex min-w-0 items-center gap-1 rounded-md p-1">
                        <span className="min-w-0 truncate text-left text-base font-bold">{displayName}</span>
                    </div>
                )}
            </div>
            {!isDetailsPanelOpen && (
                <div className="flex items-center justify-end gap-1">
                    {isMobile ? (
                        <Tooltip content="Show environment details">
                            <Button
                                variant="ghost"
                                className="rounded-[4px] border-0 p-0"
                                size="lg"
                                onClick={onToggleDetailsPanel}
                                LeadingIcon={IconPanelRightOpen}
                                aria-label="Show environment details"
                                data-tracking-id="show-environment-details-agent-execution-conversation"
                            />
                        </Tooltip>
                    ) : (
                        <Tooltip
                            content={
                                <div className="flex flex-row items-center gap-2">
                                    <span>Show details panel</span>
                                    <KeyboardCombo keys={ToggleDetailsPanelKeyBinding} />
                                </div>
                            }
                        >
                            <Button
                                variant="ghost"
                                className="rounded-[4px] border-0 p-2"
                                size="sm"
                                onClick={onToggleDetailsPanel}
                                aria-label="Show right panel"
                                data-tracking-id="toggle-details-panel-agent-execution-conversation"
                            >
                                <SidebarIconClosed size="sm" />
                            </Button>
                        </Tooltip>
                    )}
                </div>
            )}
        </div>
    );
};
